/**
 * Accepted proposals → estimate lines.
 *
 * The step between "the estimator said yes" and "there is money on the screen".
 * Pure, because this is where the include/exclude rules fire and those are the
 * ones that quietly earn or lose a shop money.
 *
 * Two things happen here that a shop would recognise:
 *   - a part that needs finishing brings its refinish line automatically (FR30)
 *   - what an operation does NOT cover is surfaced as a prompt, not silently
 *     omitted and not silently added (FR29)
 */

import type { Proposal } from '../ai/review.js';
import type { EstimateLine, LabourType, PartType } from '../calc/types.js';

/** A price the shop already knows, from its own catalog. */
export interface CatalogEntry {
  partNumber?: string;
  description: string;
  partType: PartType;
  price: number;
  /**
   * The shop's own times for this panel. All optional, all nullable in spirit:
   * absent means "nobody has said yet" and the app asks. Zero would mean "this
   * takes no time", which is a different and usually wrong claim.
   *
   *   replaceHours   fitting a new one (R&R)
   *   repairHours    taking the dent out instead — no part is charged
   *   refinishHours  painting it, which also drives paint materials
   */
  replaceHours?: number;
  repairHours?: number;
  refinishHours?: number;
  /**
   * Which body style this row prices. Absent means "any vehicle".
   *
   * A pickup hood is bigger, dearer and slower to fit and paint than a sedan
   * hood. A shop pricing both from one row under-charges every truck.
   */
  bodyStyle?: BodyStyle;
  /**
   * Which marque this row prices. Absent means "any make".
   *
   * A Toyota bumper cover and a Mercedes bumper cover are the same words and
   * nothing like the same money. Make, not model — model is thousands of rows
   * nobody can keep current, and a stale catalog lies.
   */
  make?: string;
}

/** The same five the vehicle record uses, so no translation is needed. */
export type BodyStyle = 'sedan' | 'pickup' | 'suv' | 'van' | 'other';

/** What an operation leaves out, in this shop's own words (FR29). */
export interface ExclusionRule {
  /** Operation this applies to, e.g. 'replace'. */
  operation: string;
  /** Matched case-insensitively against the part label. */
  partPattern: string;
  /** Plain-language description of what is not covered. */
  excludes: string;
  /** The line to offer if the estimator accepts the prompt. */
  suggests: Omit<EstimateLine, 'id'>;
}

export interface BuildInput {
  proposals: Proposal[];
  /** Verdict per proposal key. Only 'accepted' produces lines. */
  verdicts: Record<string, 'pending' | 'accepted' | 'rejected' | 'dismissed'>;
  catalog: CatalogEntry[];
  exclusions?: ExclusionRule[];
  /**
   * Refinish hours per accepted part, keyed the same way as verdicts. Supplied
   * by the shop or the estimator — never guessed, because no photograph
   * contains an hours figure.
   */
  refinishHours?: Record<string, number>;
  /** Prompts the estimator has already answered, so they do not reappear. */
  dismissedPrompts?: string[];
  /** This vehicle's body style, so the right catalog rows are chosen. */
  bodyStyle?: BodyStyle;
  /** This vehicle's make, for the same reason. */
  make?: string;
}

/** A question the estimate is waiting on, rather than an answer it invented. */
export type Prompt =
  | { kind: 'missing_price'; key: string; partLabel: string; partNumber?: string;
      /**
       * Rows with this name that were REJECTED for the wrong vehicle.
       *
       * "Not in your catalog" is a lie when the shop can see a hood in their
       * catalog. The truthful version is "you have two hoods, both Toyota, and
       * this is a BMW" — which tells them what to do about it.
       */
      nearMisses?: Array<{ description: string; bodyStyle?: string; make?: string;
                           price: number }> }
  | { kind: 'exclusion'; key: string; partLabel: string; excludes: string;
      suggests: Omit<EstimateLine, 'id'> };

export interface BuildResult {
  lines: EstimateLine[];
  prompts: Prompt[];
}

const keyOf = (p: Proposal) => `${p.zone}::${p.partLabel}`;

/** Operations that put a part on the estimate rather than only labour. */
const PART_OPERATIONS = new Set(['replace']);

/**
 * The shop's own time for this operation on this panel, if it has one.
 *
 * A repair takes the dent out and charges NO part; a replace fits a new one.
 * They are different numbers and were both being typed from memory every job.
 */
function catalogHours(entry: CatalogEntry | undefined, operation: string): number {
  if (!entry) return 0;
  if (operation === 'replace') return entry.replaceHours ?? 0;
  if (operation === 'repair') return entry.repairHours ?? 0;
  return 0;
}

/**
 * Find the row that prices this part for THIS vehicle.
 *
 * Four passes, most specific first:
 *   1. exact name + this body style
 *   2. exact name, any vehicle
 *   3. contained name + this body style
 *   4. contained name, any vehicle
 *
 * The fallback to a generic row is what lets a shop split its catalog
 * gradually — panel by panel, as they discover which ones actually differ —
 * instead of categorising four hundred rows before the product is useful.
 */
function findInCatalog(
  catalog: CatalogEntry[], label: string, bodyStyle?: BodyStyle, make?: string,
): CatalogEntry | undefined {
  const needle = label.trim().toLowerCase();
  const named = (c: CatalogEntry) => c.description.trim().toLowerCase();
  const exact = (c: CatalogEntry) => named(c) === needle;
  const loose = (c: CatalogEntry) => needle.includes(named(c));

  const mk = make?.trim().toLowerCase();
  const sameMake = (c: CatalogEntry) =>
    Boolean(mk) && c.make?.trim().toLowerCase() === mk;
  const anyMake = (c: CatalogEntry) => !c.make;
  const sameStyle = (c: CatalogEntry) => Boolean(bodyStyle) && c.bodyStyle === bodyStyle;
  const anyStyle = (c: CatalogEntry) => !c.bodyStyle;

  // Most specific first. Make is ranked above body style: a marque changes the
  // part PRICE, which is usually the bigger number, where body style mostly
  // changes hours.
  const ranked: Array<(c: CatalogEntry) => boolean> = [
    (c) => sameMake(c) && sameStyle(c),
    (c) => sameMake(c) && anyStyle(c),
    (c) => anyMake(c) && sameStyle(c),
    (c) => anyMake(c) && anyStyle(c),
  ];
  for (const match of [exact, loose]) {
    for (const scope of ranked) {
      const hit = catalog.find((c) => match(c) && scope(c));
      if (hit) return hit;
    }
  }
  return undefined;
}

function labourTypeFor(p: Proposal): LabourType {
  if (p.workKind === 'mechanical') return 'mechanical';
  return 'body';
}

/**
 * Build the lines.
 *
 * Hours are deliberately zero unless supplied. The engine will happily total a
 * zero-hour line; what it must never do is invent one, because that is the
 * number the shop is paid on and no photograph contains it.
 */
export function buildEstimate(input: BuildInput): BuildResult {
  const { proposals, verdicts, catalog } = input;
  const dismissed = new Set(input.dismissedPrompts ?? []);
  const lines: EstimateLine[] = [];
  const prompts: Prompt[] = [];
  let n = 0;

  const accepted = proposals.filter((p) => verdicts[keyOf(p)] === 'accepted' && !p.blocked);

  for (const p of accepted) {
    const key = keyOf(p);
    const wantsPart = PART_OPERATIONS.has(p.operation);
    // Looked up for EVERY operation now, not only the ones that carry a part.
    // A repair has no part but it does have a time, and that time lives here.
    const entry = findInCatalog(catalog, p.partLabel, input.bodyStyle, input.make);

    if (wantsPart && !entry) {
      // Ask once, keep the answer (FR24). The line still goes on at zero so the
      // estimate is not silently short.
      const needle = p.partLabel.trim().toLowerCase();
      const nearMisses = catalog
        .filter((c) => {
          const name = c.description.trim().toLowerCase();
          return name === needle || needle.includes(name) || name.includes(needle);
        })
        .map((c) => ({ description: c.description, bodyStyle: c.bodyStyle,
                       make: c.make, price: c.price }));
      prompts.push({
        kind: 'missing_price', key, partLabel: p.partLabel,
        ...(nearMisses.length > 0 ? { nearMisses } : {}),
      });
    }

    lines.push({
      id: `${key}#part`,
      operation: p.operation,
      description: p.partLabel,
      labourType: labourTypeFor(p),
      partType: wantsPart ? (entry?.partType ?? 'new_oem') : 'existing',
      partNumber: wantsPart ? entry?.partNumber : undefined,
      // A repair charges no part, however good the catalog price is.
      partPrice: wantsPart ? (entry?.price ?? 0) : 0,
      quantity: 1,
      // The shop's own time if it has one; still zero if nobody has said.
      // The estimator can always overrule it on the Price screen.
      hours: catalogHours(entry, p.operation),
      priceSource: !wantsPart ? 'none' : (entry ? 'catalog' : 'none'),
      ...(catalogHours(entry, p.operation) > 0
        ? { hoursSource: 'catalog' as const } : {}),
      // Only when the proposal is a saved database row. A fixture or an
      // in-flight read that has not persisted yet carries no id, and the line
      // is simply not traceable back to evidence — never a fabricated one.
      ...(p.id ? { proposalId: p.id } : {}),
    });
    n += 1;

    // Refinish follows the part. The estimator's figure for THIS job wins; the
    // catalog's is the shop's standing time for that panel, used when they have
    // not said otherwise.
    const refinish = input.refinishHours?.[key] ?? entry?.refinishHours;
    if (refinish && refinish > 0) {
      lines.push({
        id: `${key}#refinish`,
        operation: 'refinish',
        description: `Refinish — ${p.partLabel}`,
        labourType: 'refinish',
        partType: 'none',
        partPrice: 0,
        quantity: 1,
        hours: refinish,
        priceSource: 'none',
        // The estimator's figure for this job wins over the catalog's, and the
        // badge must say which one is actually on the line.
        hoursSource: input.refinishHours?.[key] !== undefined ? 'typed' : 'catalog',
      });
      n += 1;
    }

    // What this operation does not cover.
    for (const rule of input.exclusions ?? []) {
      if (rule.operation !== p.operation) continue;
      if (!p.partLabel.toLowerCase().includes(rule.partPattern.toLowerCase())) continue;
      const promptKey = `${key}::${rule.excludes}`;
      if (dismissed.has(promptKey)) continue;
      prompts.push({
        kind: 'exclusion', key: promptKey, partLabel: p.partLabel,
        excludes: rule.excludes, suggests: rule.suggests,
      });
    }
  }

  return { lines, prompts };
}
